/**
 * AWARE - complimentary gift thresholds
 *
 * Keeps free gift line items in sync with the cart. Each gift has its own
 * threshold, so gifts ladder: the tote on any purchase, the gua sha above an
 * amount. A threshold of 0 means any purchase, but never an empty cart and
 * never a cart holding nothing but gifts.
 *
 * Shopify's automatic discounts are what actually make the gifts free. This
 * script only puts the items in the cart so those discounts can apply.
 *
 * Two platform facts drive the design, both verified against the live store:
 *
 *   1. Shopify applies only ONE automatic discount per cart. The upper tier is
 *      therefore a single discount granting BOTH gifts.
 *   2. That discount only applies once BOTH entitled units are present. So
 *      membership is assembled FIRST and prices are checked AFTER, never per
 *      gift as it is added.
 *
 * Safety policy: if a promotional gift is found at a nonzero price, remove it
 * on a best-effort background pass. Checkout is never delayed or blocked while
 * that happens. Unmarked copies are ordinary products the shopper chose to buy.
 */
(function () {
  'use strict';

  var CFG = window.AWARE_FREE_GIFT;
  if (!CFG || !CFG.gifts || !CFG.gifts.length) return;

  // When the promotion is switched off or reaches its configured cutoff, we
  // still recognise gifts already in shoppers' carts so they can be cleaned
  // out. In that mode nothing is ever added.
  var PROMOTION_DISABLED = CFG.enabled === false;
  var END_AT_MS = Number(CFG.endsAt) * 1000 || 0;
  var serverNowMs = Number(CFG.serverNow) * 1000 || 0;
  var clockOffsetMs = serverNowMs ? serverNowMs - Date.now() : 0;

  function promotionEnded() {
    return END_AT_MS > 0 && Date.now() + clockOffsetMs >= END_AT_MS;
  }
  // SnapB2B also wraps window.fetch for tagged wholesale customers. They are
  // excluded from the offer, so perform the page-load cleanup below but leave
  // their fetch implementation untouched after that.
  var CUSTOMER_EXCLUDED = CFG.customerExcluded === true;

  var rate = (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;

  var GIFTS = [];
  for (var g = 0; g < CFG.gifts.length; g++) {
    var id = Number(CFG.gifts[g].variantId);
    var amount = Number(CFG.gifts[g].thresholdAmount);
    if (!id || isNaN(amount) || amount < 0) continue;
    // Convert to presentment cents BEFORE rounding. Rounding to whole currency
    // units first loses up to half a unit against Shopify's own threshold.
    GIFTS.push({ id: id, threshold: Math.round(amount * rate * 100) });
  }
  if (!GIFTS.length) return;

  var GIFT_IDS = GIFTS.map(function (gift) { return gift.id; });
  var GIFT_PROPERTY = '_aware_free_gift';
  var GIFT_PROPERTY_VALUE = 'true';
  var IDENTITY_ATTRIBUTE = '_aware_free_gift_identity';
  var IDENTITY_VERSION = 'v1';

  var nativeFetch = window.fetch.bind(window);
  var busy = false;
  var dirty = false;
  var activeReconcile = null;

  // An internal request can be intercepted by an app or lost on a bad
  // connection. Never let it hold an already-committed theme request forever.
  var REQUEST_TIMEOUT_MS = 5000;

  var themeRoutes = (window.theme && window.theme.routes && window.theme.routes.cart) || {};
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var PATHS = {
    get: (themeRoutes.base || root + 'cart') + '.js',
    add: (themeRoutes.add || root + 'cart/add') + '.js',
    change: (themeRoutes.change || root + 'cart/change') + '.js',
    update: (themeRoutes.update || root + 'cart/update') + '.js'
  };

  // sessionStorage throws in some privacy modes. Fall back to memory so the
  // flags still work for the life of the page instead of silently reading as
  // unset, which would re-add gifts the shopper deleted and could loop.
  var memory = {};
  function flag(key, value) {
    if (value === undefined) {
      try {
        var stored = sessionStorage.getItem(key);
        return stored === null ? (key in memory ? memory[key] : null) : stored;
      } catch (e) { return key in memory ? memory[key] : null; }
    }
    if (value === null) { delete memory[key]; try { sessionStorage.removeItem(key); } catch (e) {} return null; }
    memory[key] = value;
    try { sessionStorage.setItem(key, value); } catch (e) {}
    return null;
  }

  function settleWithin(promise, milliseconds, fallback, onTimeout) {
    return new Promise(function (resolve) {
      var settled = false;
      var timer = setTimeout(function () {
        if (settled) return;
        settled = true;
        if (onTimeout) onTimeout();
        resolve(fallback);
      }, milliseconds);

      Promise.resolve(promise).then(function (value) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(value);
      }, function () {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(fallback);
      });
    });
  }

  /* Returns the parsed body on success, or null on ANY failure including a
     non-2xx status or timeout. A timed-out mutation might still have committed,
     so callers obtain a fresh cart before they declare it safe. */
  function request(path, body) {
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var options = {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    };
    if (body) options.body = JSON.stringify(body);
    if (controller) options.signal = controller.signal;
    var pending = nativeFetch(path, options).then(function (response) {
      if (!response || !response.ok) return null;
      return response.json().catch(function () { return null; });
    }).catch(function () { return null; });
    return settleWithin(pending, REQUEST_TIMEOUT_MS, null, function () {
      if (controller) controller.abort();
    });
  }

  function isPromotionalGift(item) {
    return GIFT_IDS.indexOf(item.variant_id) !== -1 &&
      item.properties && item.properties[GIFT_PROPERTY] === GIFT_PROPERTY_VALUE;
  }

  /* Shopify can split one promotional gift across MORE THAN ONE line when its
     quantity exceeds what the discount covers. Always handle every marked line. */
  function linesFor(cart, giftId) {
    return cart.items.filter(function (item) {
      return item.variant_id === giftId && isPromotionalGift(item);
    });
  }

  function amountCharged(lines) {
    return lines.reduce(function (n, line) { return n + line.final_line_price; }, 0);
  }

  /* Exclude only script-added gifts. An unmarked gift product is an ordinary
     purchase and contributes its final price like every other paid product. */
  function qualifyingTotal(cart) {
    return cart.items.reduce(function (sum, item) {
      return isPromotionalGift(item) ? sum : sum + item.final_line_price;
    }, 0);
  }

  // Record partial success as a cart change even if a later line fails. This
  // keeps the rendered cart from going stale after any successful removal.
  function removeLines(lines) {
    return lines.reduce(function (chain, line) {
      return chain.then(function (state) {
        return request(PATHS.change, { id: line.key, quantity: 0 }).then(function (result) {
          return {
            changed: state.changed || result !== null,
            ok: state.ok && result !== null
          };
        });
      });
    }, Promise.resolve({ changed: false, ok: true }));
  }

  /* Older releases identified gifts by variant ID alone. Before allowing
     unmarked variants to behave as paid products, remove those legacy lines
     once and persist a cart-level migration marker. New carts are marked while
     empty, before a shopper can add a paid gift product. */
  function ensureGiftIdentity(cart) {
    var attributes = cart.attributes || {};
    if (attributes[IDENTITY_ATTRIBUTE] === IDENTITY_VERSION) {
      return Promise.resolve({ cart: cart, changed: false, safe: true });
    }

    var legacyLines = cart.items.filter(function (item) {
      return GIFT_IDS.indexOf(item.variant_id) !== -1 && !isPromotionalGift(item);
    });

    for (var l = 0; l < legacyLines.length; l++) {
      var legacyId = legacyLines[l].variant_id;
      flag('aware:gift:added:' + legacyId, null);
      flag('aware:gift:dismissed:' + legacyId, null);
      flag('aware:gift:blocked:' + legacyId, null);
    }

    return removeLines(legacyLines).then(function (removed) {
      if (!removed.ok) return { cart: null, changed: removed.changed, safe: false };

      var identityAttributes = {};
      identityAttributes[IDENTITY_ATTRIBUTE] = IDENTITY_VERSION;
      return request(PATHS.update, { attributes: identityAttributes }).then(function (updated) {
        if (updated && updated.items && updated.attributes &&
            updated.attributes[IDENTITY_ATTRIBUTE] === IDENTITY_VERSION) {
          return { cart: updated, changed: removed.changed, safe: true };
        }

        // The update response can be lost after Shopify commits it. One fresh
        // read distinguishes that case from a failed migration.
        return request(PATHS.get).then(function (fresh) {
          var marked = fresh && fresh.items && fresh.attributes &&
            fresh.attributes[IDENTITY_ATTRIBUTE] === IDENTITY_VERSION;
          return { cart: marked ? fresh : null, changed: removed.changed, safe: !!marked };
        });
      });
    });
  }

  function addGift(gift) {
    var properties = {};
    properties[GIFT_PROPERTY] = GIFT_PROPERTY_VALUE;
    return request(PATHS.add, {
      items: [{ id: gift.id, quantity: 1, properties: properties }]
    }).then(function (result) {
      // Sold out, blocked by another app, or otherwise rejected. Stay silent.
      return result !== null;
    });
  }

  /* Phase 1. Make membership match the thresholds, working from ONE cart
     snapshot. Deliberately does not look at prices: the upper tier's discount
     needs both gifts present before it applies, so judging a gift before its
     partner arrives would wrongly rip it back out. */
  function assemble(cart) {
    var total = qualifyingTotal(cart);
    var changed = false;
    var safe = true;

    return GIFTS.reduce(function (chain, gift) {
      return chain.then(function () {
        var lines = linesFor(cart, gift.id);
        var keyAdded = 'aware:gift:added:' + gift.id;
        var keyDismissed = 'aware:gift:dismissed:' + gift.id;
        var keyBlocked = 'aware:gift:blocked:' + gift.id;
        var qualifies = !PROMOTION_DISABLED && !promotionEnded() && total > 0 && total >= gift.threshold;

        if (!qualifies) {
          flag(keyAdded, null);
          flag(keyDismissed, null);
          flag(keyBlocked, null);
          if (!lines.length) return;
          return removeLines(lines).then(function (result) {
            if (result.changed) changed = true;
            if (!result.ok) safe = false;
          });
        }

        // Already exactly one unit. Price is checked in phase 2.
        if (lines.length === 1 && lines[0].quantity === 1) {
          flag(keyAdded, '1');
          return;
        }

        // Split across lines, or quantity raised above one. Rebuild it.
        if (lines.length) {
          return removeLines(lines).then(function (result) {
            if (result.changed) changed = true;
            if (!result.ok) { safe = false; return; }
            return addGift(gift).then(function (added) {
              if (added) flag(keyAdded, '1');
              else safe = false;
            });
          });
        }

        // Absent. If we put it there before, the shopper took it out.
        if (flag(keyAdded) || flag(keyDismissed)) {
          flag(keyDismissed, '1');
          return;
        }

        // Phase 2 removed it at this exact cart value. Do not retry until the
        // cart actually changes, or every interaction would re-add and re-remove.
        if (flag(keyBlocked) === String(total)) return;

        return addGift(gift).then(function (added) {
          if (!added) { safe = false; return; }
          changed = true;
          flag(keyAdded, '1');
        });
      });
    }, Promise.resolve()).then(function () { return { changed: changed, safe: safe }; });
  }

  /* Phase 2. The assembled set must be entirely free. Anything still charged is
     removed. Removing one gift can cost the other its discount, so this
     re-checks after each removal round. */
  function enforceFree(cart) {
    var changed = false;
    var safe = true;

    function chargedIn(c) {
      var out = [];
      for (var i = 0; i < GIFTS.length; i++) {
        var lines = linesFor(c, GIFTS[i].id);
        if (lines.length && amountCharged(lines) > 0) out.push({ gift: GIFTS[i], lines: lines });
      }
      return out;
    }

    function pass(current, remaining) {
      var charged = chargedIn(current);
      if (!charged.length) return Promise.resolve();
      if (remaining <= 0) { safe = false; return Promise.resolve(); }
      var total = qualifyingTotal(current);

      return charged.reduce(function (chain, item) {
        return chain.then(function () {
          return removeLines(item.lines).then(function (result) {
            if (result.changed) changed = true;
            if (!result.ok) return;
            // We removed it, the shopper did not, so `added` must not linger or
            // the next pass would read it as a deliberate removal.
            flag('aware:gift:added:' + item.gift.id, null);
            flag('aware:gift:blocked:' + item.gift.id, String(total));
          });
        });
      }, Promise.resolve()).then(function () {
        return request(PATHS.get).then(function (fresh) {
          if (!fresh || !fresh.items) { safe = false; return; }
          return pass(fresh, remaining - 1);
        });
      });
    }

    return pass(cart, GIFTS.length + 1).then(function () {
      return { changed: changed, safe: safe };
    });
  }

  function runOnce(snapshot) {
    var cartPromise = snapshot && snapshot.items ? Promise.resolve(snapshot) : request(PATHS.get);

    return cartPromise.then(function (cart) {
      if (!cart || !cart.items) return { changed: false, safe: false };
      return ensureGiftIdentity(cart).then(function (identity) {
        if (!identity.safe || !identity.cart) {
          return { changed: identity.changed, safe: false };
        }
        return assemble(identity.cart).then(function (assembled) {
          if (!assembled.changed && assembled.safe) {
            // Nothing moved, so the snapshot is still accurate. This is the common
            // case and it costs exactly one round trip after the one-time migration.
            return enforceFree(identity.cart).then(function (enforced) {
              enforced.changed = identity.changed || enforced.changed;
              return enforced;
            });
          }
          return request(PATHS.get).then(function (fresh) {
            var changed = identity.changed || assembled.changed;
            if (!fresh || !fresh.items) return { changed: changed, safe: false };
            return enforceFree(fresh).then(function (enforced) {
              return {
                changed: changed || enforced.changed,
                safe: enforced.safe
              };
            });
          });
        });
      });
    }).catch(function () { return { changed: false, safe: false }; });
  }

  /* A mutation landing while we are mid-flight would otherwise be dropped,
     leaving the cart judged against a stale total. Remember it and run again. */
  function reconcile(snapshot) {
    if (busy) { dirty = true; return activeReconcile || Promise.resolve({ changed: false, safe: false }); }
    busy = true;
    var changedAny = false;
    var safe = false;
    var firstSnapshot = snapshot;

    function loop() {
      dirty = false;
      var currentSnapshot = firstSnapshot;
      firstSnapshot = null;
      return runOnce(currentSnapshot).then(function (result) {
        if (result.changed) changedAny = true;
        safe = result.safe;
        if (dirty) return loop();
      });
    }

    activeReconcile = loop().then(function () {
      busy = false;
      activeReconcile = null;
      return { changed: changedAny, safe: safe };
    }, function () {
      busy = false;
      activeReconcile = null;
      return { changed: changedAny, safe: false };
    });
    return activeReconcile;
  }

  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  function mutationType(input, init) {
    var raw = '';
    try {
      if (typeof input === 'string') raw = input;
      else if (input instanceof URL) raw = input.href;
      else if (input && typeof input.url === 'string') raw = input.url;
      else if (input) raw = String(input);
    } catch (e) { return false; }
    if (!raw) return false;

    var parsed;
    try { parsed = new URL(raw, window.location.href); } catch (e) { return false; }
    if (parsed.origin !== window.location.origin) return false;

    var method = 'GET';
    try {
      if (init && init.method) method = String(init.method);
      else if (input && typeof input !== 'string' && input.method) method = String(input.method);
    } catch (e) {}
    if (method.toUpperCase() !== 'POST') return false;

    // Compare the path only, so a #fragment cannot hide a cart mutation.
    var match = parsed.pathname.match(CART_MUTATION);
    return match ? match[1] : false;
  }

  /* change/update/clear return the complete cart. Reuse that response as the
     first reconcile snapshot and avoid a redundant GET /cart.js. add returns
     only the added line items, so it still needs a fresh cart request. */
  function mutationSnapshot(response, type) {
    if (type === 'add') return Promise.resolve(null);
    try {
      var parsed = response.clone().json().then(function (cart) {
        return cart && cart.items ? cart : null;
      }).catch(function () { return null; });
      return settleWithin(parsed, REQUEST_TIMEOUT_MS, null);
    } catch (e) { return Promise.resolve(null); }
  }

  if (!CUSTOMER_EXCLUDED) {
    window.fetch = function (input, init) {
      var type = mutationType(input, init);
      // Our own calls use nativeFetch directly, so they never re-enter here.
      if (!type) return nativeFetch(input, init);

      return nativeFetch(input, init).then(function (response) {
        if (!response || !response.ok) return response;

        // Reconciliation is deliberately background-only. The promotion must
        // never withhold a committed cart response or delay access to checkout.
        mutationSnapshot(response, type).then(function (snapshot) {
          return reconcile(snapshot);
        }).then(function (result) {
          if (result.changed) {
            document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
          }
        }).catch(function () {
          // Reconciliation already attempts to remove charged gifts. Never turn
          // a promotion failure into a failed shopper-initiated cart request.
        });

        return response;
      });
    };
  }

  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  // Safety net for carts that arrive already out of sync: one restored from a
  // previous visit, built before the promo went live, or left behind after the
  // promo was switched off.
  onReady(function () {
    reconcile().then(function (result) {
      if (result.changed) {
        document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
      }
    });
  });

  // A fresh page rendered after the cutoff is already in cleanup-only mode.
  // This timer covers a tab left open across the cutoff. Browser timers can be
  // throttled in background tabs, so re-check the clock whenever it wakes.
  function schedulePromotionEnd() {
    if (PROMOTION_DISABLED || !END_AT_MS || promotionEnded()) return;
    var remaining = END_AT_MS - (Date.now() + clockOffsetMs);
    var delay = Math.min(Math.max(remaining, 0) + 25, 2147483647);
    setTimeout(function () {
      if (!promotionEnded()) {
        schedulePromotionEnd();
        return;
      }
      reconcile().then(function (result) {
        if (result.changed) {
          document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
        }
      });
    }, delay);
  }

  schedulePromotionEnd();
})();
